import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router';
import { useTenantCode } from './TenantCodeContext';

/**
 * A wrapper around React Router's `useNavigate` that automatically prepends
 * the tenant code prefix when navigating to absolute paths.
 *
 * Relative paths and numeric offsets (e.g. `-1`) are passed through unchanged.
 *
 * @example
 *   const navigate = usePrefixedNavigate();
 *   navigate('/dashboard');       // → '/acme/dashboard' (if tenant code is 'acme')
 *   navigate('/inbound');         // → '/acme/inbound'
 *   navigate(-1);                 // → goes back (no prefix)
 *   navigate('details');          // → relative navigation (no prefix)
 */
export function usePrefixedNavigate() {
  const navigate = useNavigate();
  const tenantCode = useTenantCode();

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      const prefixed =
        tenantCode && to.startsWith('/') ? `/${tenantCode}${to}` : to;

      navigate(prefixed, options);
    },
    [navigate, tenantCode],
  );
}
